import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Container from "@mui/material/Container";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Badge from "@mui/material/Badge";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { track, NO_CAPTURE } from "/imports/libs/analytics";
import BottomNav from "./BottomNav";
import Footer from "./Footer";
import PushSync from "./PushSync";

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { enqueueSnackbar } = useSnackbar();
  const { slug, widget, saas, cart } = useWidget();
  const { isAuthenticated, user, client, role, logout, family, activeClient, activeClientId, switchClient, loadFamily } = useAuth();

  const isTrainer = role === "trainer";

  React.useEffect(() => {
    if (isAuthenticated) loadFamily(slug);
  }, [isAuthenticated, slug, loadFamily]);

  // Show a one-shot toast handed over via navigation state. Routes that arrive
  // here through a cold widget init (e.g. password reset) can't reliably show
  // their own snackbar before this layout mounts, so they pass it along instead.
  React.useEffect(() => {
    const flash = location.state?.flash;
    if (!flash?.message) return;
    enqueueSnackbar(flash.message, { variant: flash.variant || "default" });
    const { flash: _consumed, ...rest } = location.state;
    navigate(location.pathname + location.search, { replace: true, state: rest });
  }, [location, enqueueSnackbar, navigate]);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const pathParts = location.pathname.split("/").filter(Boolean);
  const rawTab = pathParts.length > 1 ? pathParts[1] : "";
  const isCheckoutRoute = rawTab === "checkout" || pathParts.includes("checkout");
  const cartCount = cart?.items?.length || 0;

  const handleUserMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleUserMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
    navigate(`/${slug}/login`, { state: { flash: { message: "Logged out successfully.", variant: "success" } } });
  };

  const handleDashboard = () => {
    handleUserMenuClose();
    navigate(`/${slug}/${isTrainer ? "trainer" : "dashboard"}`);
  };

  // The avatar reflects whoever the user is currently acting as, so a parent
  // booking for a child sees the child's initials.
  const activePerson = activeClient || client;
  const userInitials = React.useMemo(() => {
    if (activePerson) return `${activePerson.firstName?.[0] || ""}${activePerson.lastName?.[0] || ""}`.toUpperCase();
    if (user?.email) return user.email[0].toUpperCase();
    return "";
  }, [activePerson, user]);
  const userPhoto = activePerson?.imgUrl || null;

  const handleSwitchClient = (member) => {
    switchClient(member._id);
    track("family_member_switched", { to_self: !!member.isSelf });
    handleUserMenuClose();
  };

  // ScrollToTop only reacts to a pathname change, so tapping the logo while
  // already on Home has to scroll itself.
  const goHome = () => {
    navigate(`/${slug}`);
    window.scrollTo(0, 0);
  };

  return (
    // Column flex + flex:1 content pins the footer to the viewport bottom on
    // short pages instead of letting it float mid-screen.
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      <PushSync />
      <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: "4rem", gap: 1 }}>
            {/* Brand */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", mr: "auto" }} onClick={goHome}>
              {widget?.logoUrl && (
                <Avatar
                  src={widget.logoUrl}
                  alt={saas?.name || widget?.name || "Booking"}
                  variant="rounded"
                  sx={{ width: "2.25rem", height: "2.25rem", bgcolor: "transparent" }}
                />
              )}
              <Typography variant="h6" fontWeight={700} color="primary" sx={{ whiteSpace: "nowrap" }}>
                {saas?.name || widget?.name || "Booking"}
              </Typography>
            </Box>

            {cartCount > 0 && !isCheckoutRoute && (
              <IconButton
                size="medium"
                color="default"
                onClick={() => navigate(`/${slug}/checkout`)}
                aria-label={`Cart, ${cartCount} item${cartCount === 1 ? "" : "s"}`}
              >
                <Badge badgeContent={cartCount} color="primary">
                  <ShoppingCartOutlinedIcon />
                </Badge>
              </IconButton>
            )}

            {/* Notification bell intentionally absent: nothing is wired to it
                yet, and a dead bell erodes trust. Restore when notifications ship. */}

            <IconButton
              id="person-btn"
              size="medium"
              color="default"
              onClick={isAuthenticated ? handleUserMenuOpen : () => navigate(`/${slug}/login`)}
            >
              {isAuthenticated && (userInitials || userPhoto) ? (
                <Avatar src={userPhoto || undefined} sx={{ width: "1.875rem", height: "1.875rem", bgcolor: "primary.main", fontSize: "0.75rem" }}>
                  {userInitials}
                </Avatar>
              ) : (
                <PersonOutlineIcon />
              )}
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box className={NO_CAPTURE} sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {activePerson ? `${activePerson.firstName || ""} ${activePerson.lastName || ""}`.trim() : user?.email}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {user?.email}
                </Typography>
              </Box>
              <Divider />
              {family.length > 1 && [
                <Typography key="switch-label" variant="caption" color="textSecondary" sx={{ px: 2, py: 0.5, display: "block" }}>
                  Booking for
                </Typography>,
                ...family.map((member) => (
                  <MenuItem
                    key={member._id}
                    className={NO_CAPTURE}
                    selected={member._id === activeClientId}
                    onClick={() => handleSwitchClient(member)}
                  >
                    <ListItemIcon>{member._id === activeClientId ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
                    <ListItemText primary={`${member.firstName || ""} ${member.lastName || ""}`.trim()} secondary={member.isSelf ? "You" : null} />
                  </MenuItem>
                )),
                <Divider key="switch-divider" />
              ]}
              <MenuItem onClick={handleDashboard}>
                <DashboardIcon sx={{ mr: 1 }} fontSize="small" />
                {isTrainer ? "My Schedule" : "My Dashboard"}
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} fontSize="small" />
                Sign Out
              </MenuItem>
            </Menu>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3, pb: isMobile && !isTrainer ? 10 : 3, flex: 1 }}>
        {children}
      </Container>

      <Footer />

      {/* Checkout swaps the nav for the view's own sticky total + CTA bar. */}
      {isMobile && !isTrainer && !isCheckoutRoute && <BottomNav />}
    </Box>
  );
}

export default Layout;
